import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { getLegalEntities } from '@dealstack/frontend/app/services/Person.service.ts';
import { useEffect, useMemo, useState } from 'react';
import { uuidv4 } from 'uuidv7';
import {
	GENERAL_DEAL_INPUTS_FORM,
	IMipCreateGeneralDealInputsValues,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/GeneralDealInputs/useGeneralDealInputsController.ts';
import { UseFormReturn } from 'react-hook-form';
import { CurrencyType } from '@dealstack/shared/lib/generated';

export interface EntityOption {
	label: string;
	value: string;
	entityCurrency?: CurrencyType | null;
	isNew?: boolean;
}

export const useMipEntitySelectController = (
	dealId: string,
	generalDealInputsForm: UseFormReturn<IMipCreateGeneralDealInputsValues>
) => {
	const getAllLegalEntitiesRequest = useQuery({
		queryKey: [API_PATHS.PERSON_DEAL_DEALID_LEGAL_PERSONS_GET, { dealId, onlyIssuers: true }],
		queryFn: () =>
			getLegalEntities(API_PATHS.PERSON_DEAL_DEALID_LEGAL_PERSONS_GET, {
				dealId,
				onlyIssuers: true,
			}),
	});

	const [entityOptions, setEntityOptions] = useState<EntityOption[]>([]);

	useEffect(() => {
		if (!getAllLegalEntitiesRequest?.data) {
			return;
		}

		const entitiesOptions = getAllLegalEntitiesRequest.data.map((entity) => ({
			label: entity.displayName,
			value: entity.id,
			entityCurrency: entity.entityCurrency,
		}));

		setEntityOptions(entitiesOptions);
	}, [getAllLegalEntitiesRequest?.data]);

	const onCreateNewEntity = (entityName: string) => {
		const newEntityOption = {
			label: entityName,
			value: uuidv4(),
			entityCurrency: CurrencyType.EUR,
			isNew: true,
		};

		setEntityOptions((prevOptions) => [...prevOptions, newEntityOption]);

		return newEntityOption;
	};

	const selectedEntity = useMemo(() => {
		const entityId = generalDealInputsForm.getValues(GENERAL_DEAL_INPUTS_FORM.ENTITY_ID);
		return entityOptions.find((entity) => entity.value === entityId);
	}, [generalDealInputsForm.watch(GENERAL_DEAL_INPUTS_FORM.ENTITY_ID), entityOptions]);

	return {
		isLoadingEntities: getAllLegalEntitiesRequest.isLoading,
		entityOptions,
		onCreateNewEntity,
		selectedEntity,
		refetchEntities: getAllLegalEntitiesRequest.refetch,
	};
};
